import React, { useState, useEffect, useRef, createRef } from "react";
import KissViewAndTicker from "./KissViewAndTicker";
import EmptyPanel from "./EmptyPanel";
import Tooltip from "../../_shared/components/Tooltip";
import {
  useTable,
  usePagination,
  useColumnOrder,
  useSortBy,
} from "react-table";
import FormatCell from "./FormatCell";
import FormatHeader from "./FormatHeader";
import { API, graphqlOperation } from "aws-amplify";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { useHistory } from "react-router-dom";
import TickerContextMenu from "./RightClick/TickerContextMenu";
import { isMobile } from "react-device-detect";
import "./Table.scss";
import TwitterAndArrows from "./TwitterAndArrows";
import BullseyeChat from "./BullseyeChat";

let checkForUpdateTimeout;
const tooltips = {
  "Trading Range": `A dynamic, proprietary trading range that specifies both a Low End and a High End security price, between which the security is likely to trade during the day.`,
  "Momentum Indicator": `Momentum Indicators represent key thresholds (via a price) and signals (via a Bullish / Bearish / Neutral signal) to assist investors in evaluating a security's risk.  Longbow incorporates two key time periods (Short Term  and Medium Term) for risk management.`,
  Volatility: `Statistical measure of how much a position's value fluctuates up and down.  Volatility reflects the average amount a security's price has differed from its mean over a period of time.`,
  Volume: `Total number of shares traded on a security in a day.`,
  "Price Returns": `Percent change in price of a security over specific time periods.`,
  "Short Interest": `Short interest is the total number of shares investors have sold short but have not yet bought back.`,
  "% Change in # Shares Short": `The percentage change in number of shares sold short versus the prior reporting period and versus data from one month prior.`,
  "Core Signal": `Summary signal which incorporates all Longbow proprietary KISS signals into one core signal, identifying when to buy/cover or sell/short a position.`,
  Beta: `Beta is a measure of volatility of a security price compared to another security or index’ price. Longbow calculates beta for a security versus a broad market index; it represents the percentage price change of the security given a 1% change in the broad market index.`,
  "Premium | Signal": `A proprietary signal (Premium / Neutral / Discount) that identifies how market participants appear to be positioned, based on available options and pricing data of the security.`,
  "% Equity Float": `Short interest is the total number of shares investors have sold short but have not yet bought back.Short interest as a percent of equity float is a key calculation Longbow offers, defined as the quotient of Short Interest divided by Equity Float x 100.`,
  "MONEYLINE STATS": `Use our Bullseye AI Moneyline Stats to see Longbow's proprietary AI algorithms backtested results. A stock ticker only appears on the Bullseye AI Trade Ideas list when our algorithms signal a 'Buy' or 'Sell'. When it does signal in this dashboard tab, the Moneyline Stats show the results for all previous times the ticker presented as a 'Buy' or 'Sell' in Bullseye AI Trade Ideas. These Stats only apply when Range Signal is Overbought / Oversold.`,
};
let scrollWidth = "2237px";
function Table({
  //Use the state and functions returned from useTable to build the UI.
  columns,
  data,
  fetchData,
  setRecords,
  pageCount: controlledPageCount,
  props,
  kiss,
  setRefreshKiss,
  loading,
  planInterval,
  setPlanInterval,
  alertsInterval,
  setLoading,
}) {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    visibleColumns,
    setColumnOrder,
    state: { pageIndex, pageSize, sortBy },
    //React Table hook
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0, pageSize: 200 },
      manualPagination: true,
      pageCount: controlledPageCount,
      manualSortBy: true,
    },
    //Plugin Hooks
    useColumnOrder,
    useSortBy,
    usePagination,
  );
  const topScroll = createRef();
  const tableScrollRef = createRef();
  const tableHead = useRef();
  const [refreshTable, setRefreshTable] = React.useState(new Date());
  const [hover, setHover] = React.useState({});
  const [tickerRow, setTickerRow] = React.useState(page);
  const [updatedDate, setUpdatedDate] = React.useState("");
  const [closingDate, setClosingDate] = React.useState("");
  const [includeSignal, setIncludeSignal] = useState(false);
  const [signalAlertsSubscription, setSignalAlertsSubscription] = useState(-1);
  const [clientXDelete, setClientXDelete] = useState(0);
  const [clientYDelete, setClientYDelete] = useState(0);
  const [touchXDelete, setTouchXDelete] = useState(0);
  const [touchYDelete, setTouchYDelete] = useState(0);
  const [touchTarget, setTouchTarget] = useState("");
  const [contextMenu, setContextMenu] = useState(false);
  const [cellIndex, setCellIndex] = useState(0);
  const [compactMode, setCompactMode] = useState(
    localStorage.getItem("compact") ? true : false,
  );
  const [scrollOffset, setScrollOffset] = useState(0);

  //there are a bug where if you are inside of a modal in a non-longbow tab and you right click,
  //the delete button will pop up. this state below fixes that
  const [disableDeleteContextMenu, setDisableDeleteContextMenu] =
    useState(false);

  const history = useHistory();

  const checkLastUpdated = async () => {
    try {
      const data = await API.post("auth", "/cognito/checkLastUpdated");
      setUpdatedDate(data.lastPriceDate);
      setClosingDate(data.closingPriceDate);

      const tickersLeft = (
        await API.graphql(
          graphqlOperation(`{
        get_tickers_left {
          tickers_left
        }
      }`),
        )
      ).data?.get_tickers_left.tickers_left;
      if (tickersLeft < 0) {
        //Redirect user to manage subscriptions, open up remove-tickers-modal.
        // history.push("/account/manage-subscriptions?downgrade=true");
      }
    } catch (err) {
      console.log(err);
    }
  };
  const checkForUpdate = async () => {
    //Check for updates M-F at 10:21am, 11:21am, 12:21pm, 01:21pm, 02:21pm, 03:21pm, and 04:47pm EST.
    clearTimeout(checkForUpdateTimeout);
    const date = new Date();
    const hours = date.getUTCHours();
    const minutes = date.getUTCMinutes();
    if (
      ![0, 6].includes(date.getUTCDay()) &&
      ((hours >= 15 && hours < 21 && minutes === 21) ||
        (hours >= 15 && hours < 21 && minutes === 35) ||
        (hours === 21 && minutes === 47))
    ) {
      setRefreshTable(new Date());
      checkLastUpdated();
    }
    checkForUpdateTimeout = setTimeout(() => {
      checkForUpdate();
    }, 60000);
  };

  useEffect(() => {
    const getData = async () => {
      const response = await API.graphql(
        graphqlOperation(`{
      subscriptions {
        id
        name
        monthly_daily_price
        monthly_daily_price_id
        monthly_intraday_price
        monthly_intraday_price_id
        subscribed {
          id
          subscription_id
          type
          frequency
          price_id
          expires
        }
      }
    }`),
      );

      setSignalAlertsSubscription(
        response.data.subscriptions.filter(
          (obj) => obj.id === "signal_alerts",
        )[0].subscribed,
      );
      //Include access to 'Signal Alerts' add-on if subscribed to 'Master Plan'.
      setIncludeSignal(
        response.data.subscriptions[4].subscribed ? true : false,
      );
    };
    getData();
    checkForUpdate();
    checkLastUpdated();
    return () => {
      clearTimeout(checkForUpdateTimeout);
    };
  }, []);

  useEffect(() => {
    setTickerRow(page);
  }, [page]);

  //Get the touch coordinates to set the 'delete' button placement on drag-drop. (only on touch devices)
  useEffect(() => {
    document.addEventListener("touchstart", setCoordinates);
    return () => {
      document.removeEventListener("touchstart", setCoordinates);
    };
  }, [touchXDelete, touchYDelete]);

  function setCoordinates(e) {
    setTouchYDelete(e.changedTouches[0].clientY);
    setTouchXDelete(e.changedTouches[0].clientX);
    setTouchTarget(e.target.className);
  }

  //Get compact mode status on load from local storage.
  useEffect(() => {
    localStorage.setItem("compact", compactMode ? "compact" : "");
  }, [compactMode]);

  useEffect(() => {
    fetchData({ tab: props.data, pageIndex, pageSize });
  }, [fetchData, pageIndex, pageSize, refreshTable, props.data]);

  useEffect(() => {
    if (sortBy.length > 0 && columns.length > 0) {
      const parsedColumns = [];
      for (const column of columns) {
        for (const subColumn of column.columns) {
          parsedColumns.push(subColumn);
        }
      }
      const accessor = parsedColumns.find(
        (c) => c.id === sortBy[0].id,
      )?.accessor;
      if (!accessor) return;
      fetchData({
        tab: props.data,
        pageIndex,
        pageSize,
        sortBy: `${accessor}: ${sortBy[0].desc ? "asc" : "desc"}`,
      });
    }
  }, [sortBy]);

  const handleClick = React.useCallback(
    (e) => {
      contextMenu && setContextMenu(false);
    },
    [contextMenu],
  );

  React.useEffect(() => {
    //Hide the 'delete' button on scroll.
    document.addEventListener(
      "scroll",
      () => contextMenu && setContextMenu(false),
    );
    return () => {
      //Return a cleanup function to unregister the function since its gonna run multiple times.
      document.removeEventListener(
        "scroll",
        () => contextMenu && setContextMenu(false),
      );
    };
  }, [contextMenu]);

  //Hide 'delete' button (contextMenu) when clicking anywhere on the page.
  React.useEffect(() => {
    document.addEventListener("click", handleClick);
    return () => {
      document.addEventListener("click", handleClick);
    };
  });
  //keeps track of when table width changes so can update scrollWidth
  // useEffect(() => {
  //   if (tableHead.current?.clientWidth) onTopScroll();
  // }, [kiss, onTopScroll]);

  let columnBeingDragged = null;

  const onDragStart = (e) => {
    columnBeingDragged = e.target.dataset.columnIndex;
  };

  const onDrop = async (e) => {
    e.preventDefault();
    if (e.target.attributes.draggable.textContent === "true") {
      const newPosition = e.target.dataset.columnIndex;
      let currentCols = visibleColumns.map((c) => c.id);
      const oldPositionColumns = headerGroups[0].headers[
        columnBeingDragged
      ].headers.map((c) => c.id);
      const newPositionColumns = headerGroups[0].headers[
        newPosition
      ].headers.map((c) => c.id);

      currentCols = currentCols.filter(
        (item) => !oldPositionColumns.includes(item),
      );

      function insertArrayAt(array, index, arrayToInsert) {
        Array.prototype.splice.apply(array, [index, 0].concat(arrayToInsert));
        return array;
      }
      if (newPosition < columnBeingDragged) {
        insertArrayAt(
          currentCols,
          currentCols.indexOf(newPositionColumns[0]),
          oldPositionColumns,
        );
      } else {
        insertArrayAt(
          currentCols,
          currentCols.indexOf(
            newPositionColumns[newPositionColumns.length - 1],
          ) + 1,
          oldPositionColumns,
        );
      }
      setColumnOrder(currentCols);
      // console.log(currentCols);
      // await API.graphql(
      //   graphqlOperation(`mutation{
      //   ${currentCols.map(
      //     (obj, i) =>
      //       `update${i}: update_column_by_pk(pk_columns: {id: "${obj}"}, _set: {order: ${i}}) {id}`
      //   )}
      // }`)
      // );
    }
  };

  // let rowBeingDragged = null;

  let percentFromHeaderTotal = 0;
  let header30DTotal = 0;
  let header90DTotal = 0;
  let headerVolatilityTotal = 0;
  // let headerVolatilityTotalWordLength = 0;
  let headerRealizedVolatilityTotal = 0;
  // let displayPercentFromHeader = true;
  let display30DHeader = true;
  let display90DHeader = true;
  let displayVolatilityHeader = true;
  let displayRealizedVolatilityHeader = true;

  if (columns.length >= 3) {
    for (let i = 0; i < columns[2].columns.length; i++) {
      if (
        columns[2].columns[i]?.Header === "% from Trade Range | Low" ||
        columns[2].columns[i]?.Header === "% from Trade Range | High"
      )
        percentFromHeaderTotal += 1;
    }
  }

  if (columns.length >= 4) {
    for (let i = 0; i < columns[3].columns.length; i++) {
      if (
        columns[3].columns[i]?.Header === "S/T Momentum Signal" ||
        columns[3].columns[i]?.Header === "S/T Momentum Price"
      )
        header30DTotal += 1;

      if (
        columns[3].columns[i]?.Header === "M/T Momentum Signal" ||
        columns[3].columns[i]?.Header === "M/T Momentum Price"
      )
        header90DTotal += 1;
    }
  }
  if (columns.length >= 5) {
    for (let i = 0; i < columns[4]?.columns.length; i++) {
      if (
        columns[4].columns[i]?.Header === "Premium | Signal" ||
        columns[4].columns[i]?.Header === "Premium | Current" ||
        columns[4].columns[i]?.Header === "Premium | 1W" ||
        columns[4].columns[i]?.Header === "Premium | ST <30D" ||
        columns[4].columns[i]?.Header === "Premium | MT >90D"
      ) {
        headerVolatilityTotal += 1;
        // headerVolatilityTotalWordLength +=
        //   columns[4].columns[i]?.Header.split(" ").slice(-1)[0].length;
      } else if (
        columns[4].columns[i]?.Header === "Realized | Current" ||
        columns[4].columns[i]?.Header === "Realized | 1W" ||
        columns[4].columns[i]?.Header === "Realized | 1M" ||
        columns[4].columns[i]?.Header === "Realized | 3M"
      ) {
        headerRealizedVolatilityTotal += 1;
      }
    }
  }

  // const onRowDragStart = (e) => {
  //   rowBeingDragged = e.target.dataset.rowIndex;
  // };

  // const onRowDrop = async (e) => {
  //   e.preventDefault();
  //   let newPosition = e.target.dataset.rowIndex;
  //   let newData = data.slice();
  //   newData.splice(rowBeingDragged, 1);
  //   newData.splice(newPosition, 0, data[rowBeingDragged]);
  //   setRecords(newData);
  //   //Make GraphQL call to update ticker order
  //   await API.graphql(
  //     graphqlOperation(`mutation{
  //     ${newData.map(
  //       (obj, i) =>
  //         `update${i}: update_ticker_by_pk(pk_columns: {id: "${obj.id}"}, _set: {order: ${i}}) {id}`
  //     )}
  //   }`)
  //   );
  // };

  function createCellClassName(type) {
    let result = "";
    if (type === "Sell/Short" || type === "Greed" || type === "Bearish") {
      result = "red";
    } else if (
      type === "Buy" ||
      type === "Buy/Cover" ||
      type === "Fear" ||
      type === "Bullish"
    ) {
      result = "green";
    } else if (type === "Neutral" || type === "Alert") {
      result = "grey";
    }
    return result;
  }

  //Show 'delete' button on right click (context menu).
  function showContextMenu(e, cell) {
    e.preventDefault();
    if (
      cell.column.Header === "Description" ||
      cell.column.Header === "Ticker"
    ) {
      if (!disableDeleteContextMenu) {
        setContextMenu(true);
        setClientYDelete(e.clientY);
        setClientXDelete(e.clientX);
        setCellIndex(cell.row.id);
      }
    }
  }
  //'Delete' button for touch screen devices.
  function showContextMenuTouch(cell) {
    //Make sure to show 'delete' button only on Description and Ticker columns
    if (
      touchTarget === "guidedTour3Click guidedTour4" ||
      touchTarget === "kiss-view-ticker-target"
    ) {
      setContextMenu(true);
      setClientYDelete(touchYDelete);
      setClientXDelete(touchXDelete);
      setCellIndex(cell);
    }
  }

  async function deleteTicker() {
    await API.graphql(
      graphqlOperation(`mutation {
      update_ticker_by_pk(pk_columns: {id: "${data[cellIndex].id}"}, _set: {
        deleted: "${new Date().toISOString().split("T")[0]}"
      }) {id}}`),
    );
    setRefreshTable(new Date());
  }

  function tableStyle(cell) {
    return {
      fontWeight: cell.column.Header === "Asset Class" ? "bold" : "unset",
      display: ["Ticker", "Description"].includes(cell.column.Header)
        ? null
        : "flex",
      justifyContent: ["Ticker", "Description"].includes(cell.column.Header)
        ? null
        : "center",
      margin: "auto",
      height: compactMode ? "16px" : "32px",
      color:
        cell.row.original[`red_${cell.column.Key}`] === undefined ||
        cell.row.original[`red_${cell.column.Key}`] === null
          ? "#ffffff"
          : cell.row.original[`red_${cell.column.Key}`]
          ? "#D33541"
          : "#75CE41",
      ...(cell.column.kiss &&
        !["Description", "Ticker", "Price"].includes(cell.column.Header) && {
          width: 70,
        }),
      ...(cell.column.kiss &&
        cell.column.Header === "Trading Range Low" && { textAlign: "center" }),
    };
  }
  const displayHeaderPercentFrom = (column) => {
    // if (
    //   column.Header.startsWith("% from Trade Range") &&
    //   percentFromHeaderTotal === 2 &&
    //   displayPercentFromHeader
    // ) {
    //   displayPercentFromHeader = false;
    //   return (
    //     <div
    //       style={{
    //         color: "#bbb8b8",
    //         borderBottom: "1px solid #bbb8b8",
    //         position: "absolute",
    //         top: 28,
    //         left: 16,
    //         paddingBottom: 1,
    //       }}>
    //       &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;%
    //       From&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
    //     </div>
    //   );
    // }
    return;
  };

  //MOMENTUM INDICATOR - 'Short Term' Stacked Header
  const displayHeader30D = (column) => {
    //header30DTotal keeps track of the number of columns that are not hidden in short term
    //momentum indicator tab
    if (
      column.Header.startsWith("S/T") &&
      header30DTotal === 2 &&
      display30DHeader
    ) {
      display30DHeader = false;
      return (
        <div
          style={{
            color: "#bbb8b8",
            borderBottom: "1px solid #bbb8b8",
            position: "absolute",
            top: 28,
            left: header30DTotal === 0 ? 20 : 25,
            paddingBottom: 1,
            width: header30DTotal === 0 ? 160 : "125%",
          }}
        >
          {"Short Term"}
        </div>
      );
    }
    return;
  };

  //MOMENTUM INDICATOR - 'Medium Term' Stacked Header
  const displayHeader90D = (column) => {
    //header90DTotal keeps track of the number of columns that are not hidden in medium term
    //momentum indicator tab
    if (
      column.Header.startsWith("M/T") &&
      header90DTotal === 2 &&
      display90DHeader
    ) {
      display90DHeader = false;
      return (
        <div
          style={{
            color: "#bbb8b8",
            borderBottom: "1px solid #bbb8b8",
            position: "absolute",
            top: 28,
            left: header30DTotal === 0 ? 20 : 35,
            paddingBottom: 1,
            width: header30DTotal === 0 ? 160 : "125%",
          }}
        >
          {"Medium Term"}
        </div>
      );
    }
    return;
  };
  //VOLATILITY - 'Implied Volatility' Stacked Header
  const displayHeaderVolatility = (column) => {
    if (
      column.Header.startsWith("Premium") &&
      headerVolatilityTotal > 1 &&
      displayVolatilityHeader
    ) {
      displayVolatilityHeader = false;
      //the signal header's width is much bigger than the rest so we need this variable
      //to track if the signal header is hidden or not so we can update the main header's width
      const signalHeaderWidthAdjust =
        column.parent.columns[0]?.Header === "Premium | Signal" ? 85 : 0;
      return (
        <div
          style={{
            color: "#bbb8b8",
            borderBottom: "1px solid #bbb8b8",
            //adjusting the width, font size and position of the header based on how
            //many headers are hidden

            //when there are two columns not hidden and neither is signal set top to 20
            //else if, there is at least one column hidden set top to 9
            //else set top to 28
            top:
              headerVolatilityTotal === 2 && signalHeaderWidthAdjust === 0
                ? 20
                : headerVolatilityTotal < 5
                ? 9
                : 28,
            paddingBottom: 1,
            position: "absolute",
            //when there are two columns not hidden and neither is signal have font size as 8

            fontSize:
              headerVolatilityTotal === 2 && signalHeaderWidthAdjust === 0
                ? 8
                : 12,
            width:
              headerVolatilityTotal === 2 && signalHeaderWidthAdjust === 0
                ? headerVolatilityTotal * 38
                : headerVolatilityTotal === 2
                ? headerVolatilityTotal * 29 + signalHeaderWidthAdjust
                : headerVolatilityTotal < 5
                ? headerVolatilityTotal * 38 + signalHeaderWidthAdjust
                : 270,
          }}
        >
          <div>
            Implied Volatility {headerVolatilityTotal < 5 && <br />} (Discount)
            / Premium
            <div
              data-tip={`A security’s Implied Volatility Premium/(Discount) estimates whether investors are expecting greater or less certainty for a security’s price in the future. It is a ratio equal to the percent change of its Implied Volatility to Realized Volatility.  The formula = 30D ATM Put Implied Volatility divided by 30D Price Volatility minus 1.  The results are shown as of different dates.`}
              className="info-icon"
              style={{
                fontWeight: 100,
                position: "absolute",
                top: 2,
                //when there are two columns not hidden and neither is signal have right -6
                right:
                  headerVolatilityTotal === 2 && signalHeaderWidthAdjust === 0
                    ? -6
                    : -1,
                zIndex: 1,
              }}
            >
              i
            </div>
          </div>
        </div>
      );
    }
    return;
  };

  //VOLATILITY - 'Realized Volatility' Stacked Header
  const displayHeaderRealizedVolatility = (column) => {
    if (
      column.Header.startsWith("Realized") &&
      headerRealizedVolatilityTotal > 1 &&
      displayRealizedVolatilityHeader
    ) {
      displayRealizedVolatilityHeader = false;
      return (
        <div
          style={{
            color: "#bbb8b8",
            borderBottom: "1px solid #bbb8b8",
            top: headerRealizedVolatilityTotal === 2 ? 9 : 28,
            paddingBottom: 1,
            position: "absolute",
            //the width needs to adjust based on the number of header that is visible under
            //realized volatility
            width:
              headerRealizedVolatilityTotal === 2
                ? headerRealizedVolatilityTotal * 32
                : headerRealizedVolatilityTotal === 4
                ? headerRealizedVolatilityTotal * 37
                : headerRealizedVolatilityTotal * 35,
          }}
        >
          {headerRealizedVolatilityTotal < 3 ? (
            <span>
              Realized
              <br />
              Volatility
              <div
                data-tip={`The 30-day price volatility equals the annualized standard deviation of the 
                relative price change for the 30 most recent trading days closing price, expressed as a 
                percentage. A measure of the risk of price moves for a security calculated from the standard 
                deviation of day to day logarithmic historical price changes.`}
                className="info-icon"
                style={{
                  fontWeight: 100,
                  position: "absolute",
                  top: 3,
                  zIndex: 1,
                  right: -5,
                }}
              >
                i
              </div>
            </span>
          ) : (
            <div>
              <div style={{ display: "inline-block" }}>Realized Volatility</div>
              <div
                data-tip={`The 30-day price volatility equals the annualized standard deviation of the 
                relative price change for the 30 most recent trading days closing price, expressed as a 
                percentage. A measure of the risk of price moves for a security calculated from the standard 
                deviation of day to day logarithmic historical price changes.`}
                className="info-icon"
                style={{
                  fontWeight: 100,
                  position: "absolute",
                  top: "3px",
                  zIndex: 1,
                  right: headerRealizedVolatilityTotal === 4 ? 3 : -11,
                }}
              >
                i
              </div>
            </div>
          )}
        </div>
      );
    }
    return;
  };

  //Set the 'top div scroll' to equal the table scroll.
  function onTopScroll(e) {
    scrollWidth = tableHead.current.clientWidth;
    tableScrollRef.current.scrollLeft = e?.target.scrollLeft;
  }

  //Set the table scroll to equal the 'top div scroll'.
  function onContentScroll(e) {
    topScroll.current.scrollLeft = e.target.scrollLeft;
    setScrollOffset(e.target.scrollTop);
  }

  const handleOnDragEnd = async (result) => {
    if (!result.destination) return;
    const items = Array.from(tickerRow);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    setTickerRow(items);
    await API.graphql(
      graphqlOperation(`mutation{
      ${items.map(
        (obj, i) =>
          `update${i}: update_ticker_by_pk(pk_columns: {id: "${
            obj.original.id
          }"}, _set: {order: ${items.length - i - 1}}) {id}`,
      )}
    }`),
    );
    if (isMobile) {
      if (
        tickerRow[result.source.index].allCells[0].column.Header ===
          "Description" ||
        tickerRow[result.source.index].allCells[0].column.Header === "Ticker" ||
        tickerRow[result.source.index].allCells[1].column.Header === "Ticker"
      ) {
        showContextMenuTouch(reorderedItem.index);
      }
      if (result.destination.index !== result.source.index)
        setContextMenu(false);
    }
  };
  // Render the UI for your table.
  return (
    <>
      {data.length > 0 ? (
        <div className="table">
          <span style={{ display: "none" }}>
            <label style={{ color: "gray" }}>CURRENT VIEW: </label>
            <select
              style={{
                backgroundColor: "black",
                fontWeight: "bold",
                color: "white",
                borderStyle: "none",
                fontSize: "1rem",
              }}
            >
              <option>My Tickers</option>
              <option></option>
            </select>
          </span>
          {props?.data && (
            <KissViewAndTicker
              setRefreshKiss={setRefreshKiss}
              setRefreshTable={setRefreshTable}
              tab={props.data}
              data={data}
              compactMode={compactMode}
              setCompactMode={setCompactMode}
            />
          )}
          <div
            ref={topScroll}
            onScroll={(e) => onTopScroll(e)}
            className={"scroll-wrapper"}
            style={{
              overflowX: "scroll",
            }}
          >
            <div
              className="scroll-container"
              style={{ minWidth: scrollWidth }}
            />
          </div>
          <div style={{ display: "flex", justifyContent: "flex-start" }}>
            <table
              //there are an issue where useRef() is not in sync when toggling kiss. To solve this, key
              //is set to kiss so that table will re-render and update useRef() when kiss view is toggled.
              key={kiss}
              //width is determined by tableHead because the width changes when you toggle between
              //kiss and non-kiss. Although React automatically recalculates the width, for some
              //weird reason when the table is in kiss view mode, the horizontal scrollbar will still
              //show even when the page is able to fit the table. to resolve this, the + 40 is added.
              style={{
                width: tableHead.current
                  ? localStorage.getItem("view") === "basic"
                    ? "100%"
                    : tableHead.current.clientWidth + 40
                  : 0,
              }}
              ref={tableScrollRef}
              {...getTableProps()}
              onScroll={(e) => {
                onContentScroll(e);
                setContextMenu(false);
              }}
              onLoad={(e) => {
                onContentScroll(e);
              }}
            >
              <thead ref={tableHead}>
                {headerGroups.map((headerGroup, i) => {
                  return (
                    <tr {...headerGroup.getHeaderGroupProps()}>
                      {headerGroup.headers.map((column, index) => {
                        return (
                          <th
                            // {...column.getHeaderProps({ title: undefined })}
                            {...column.getHeaderProps(
                              [
                                "Description",
                                "Ticker",
                                // "Range Low",
                                // "Range High",
                                "Range",
                                "Range Signal",
                                "Signal",
                                // "Upside",
                                // "Downside",
                                "Signal Strength",
                                "Odds",
                                "% Equity Float / Date",
                              ].includes(column.Header)
                                ? column.getSortByToggleProps()
                                : { title: undefined },
                            )}
                            //Specify different styles for columns.
                            //Certain columns will be wider and
                            //the sticky columns placement will adapt to the right
                            //position (accounting for the hidden columns)
                            style={
                              column.Header.startsWith("Realized") &&
                              column.Header.endsWith("Current")
                                ? { margin: "0", padding: "8px 14px" }
                                : column.Header.startsWith("Premium") ||
                                  column.Header.startsWith("Realized")
                                ? { margin: "0", padding: "8px 13px" }
                                : column.Header ===
                                    "% from Trade Range | Low" ||
                                  column.Header === "% from Trade Range | High"
                                ? { margin: "0", padding: "8px 2px" }
                                : ({ margin: "0px 12px 0px 0px" } &&
                                    column.Header === "Position ") ||
                                  column.Header === "Position" ||
                                  column.Header === "Description" ||
                                  column.Header === "Ticker" ||
                                  column.Header === "Closing Price" ||
                                  column.Header === "Last Price"
                                ? {
                                    position: "sticky",
                                    zIndex: 2,
                                    ...(!kiss &
                                      ((column.Header === "Ticker") &
                                        (headerGroups[1].headers[0]?.Header !==
                                          "Description")) ||
                                    kiss & (column.Header === "Ticker")
                                      ? { position: "sticky", left: 0 }
                                      : !kiss &
                                        ((column.Header === "Ticker") &
                                          (headerGroups[1].headers[0]
                                            ?.Header ===
                                            "Description"))
                                      ? { position: "sticky", left: "251px" }
                                      : !kiss &
                                        ((column.Header === "Closing Price") &
                                          (headerGroups[1].headers[0]
                                            ?.Header ===
                                            "Description"))
                                      ? { position: "sticky", left: "310px" }
                                      : kiss &
                                        (column.Header === "Closing Price") &
                                        (headerGroups[1].headers[2]?.Header !==
                                          "Last Price")
                                      ? { position: "sticky", left: 75 }
                                      : !kiss &
                                          ((column.Header === "Closing Price") &
                                            (headerGroups[1].headers[0]
                                              .Header !==
                                              "Description")) ||
                                        kiss &
                                          (column.Header === "Closing Price")
                                      ? { position: "sticky", left: "60px" }
                                      : !kiss &
                                          ((column.Header === "Last Price") &
                                            (headerGroups[1].headers[0]
                                              .Header !==
                                              "Description") &
                                            (headerGroups[1].headers[1]
                                              .Header !==
                                              "Closing Price")) ||
                                        kiss &
                                          ((column.Header === "Last Price") &
                                            (headerGroups[1].headers[1]
                                              .Header !==
                                              "Closing Price"))
                                      ? { position: "sticky", left: "80px" }
                                      : !kiss &
                                        ((column.Header === "Last Price") &
                                          (headerGroups[1].headers[0]
                                            ?.Header ===
                                            "Description") &
                                          (headerGroups[1].headers[2]
                                            ?.Header ===
                                            "Closing Price"))
                                      ? {
                                          position: "sticky",
                                          left: 386,
                                        }
                                      : !kiss &
                                          ((column.Header === "Last Price") &
                                            (headerGroups[1].headers[0]
                                              .Header !==
                                              "Description") &
                                            (headerGroups[1].headers[1]
                                              .Header ===
                                              "Closing Price")) ||
                                        kiss & (column.Header === "Last Price")
                                      ? { position: "sticky", left: 134 }
                                      : !kiss &
                                        ((column.Header === "Last Price") &
                                          (headerGroups[1].headers[0]
                                            ?.Header ===
                                            "Description") &
                                          (headerGroups[1].headers[2]
                                            ?.Header !==
                                            "Closing Price"))
                                      ? { position: "sticky", left: 257 }
                                      : { position: "sticky" }),
                                  }
                                : { position: "relative", left: 0, zIndex: 0 }
                            }
                            className={`${
                              column.depth === 0
                                ? "draggable"
                                : kiss
                                ? "non-draggable-kiss"
                                : "non-draggable"
                            } guidedTour5`}
                          >
                            {displayHeaderPercentFrom(column)}
                            {displayHeader30D(column)}
                            {displayHeader90D(column)}
                            {displayHeaderVolatility(column)}
                            {displayHeaderRealizedVolatility(column)}
                            <FormatHeader
                              column={column}
                              i={i}
                              index={index}
                              setRefreshKiss={setRefreshKiss}
                              tooltips={tooltips}
                              hover={hover}
                              setHover={setHover}
                              fetchPanels={props.fetchPanels}
                              planInterval={planInterval}
                              percentFromHeaderTotal={percentFromHeaderTotal}
                              header30DTotal={header30DTotal}
                              header90DTotal={header90DTotal}
                              headerVolatilityTotal={headerVolatilityTotal}
                              headerRealizedVolatilityTotal={
                                headerRealizedVolatilityTotal
                              }
                              onDragStart={onDragStart}
                              onDrop={onDrop}
                              closingDate={closingDate}
                              updatedDate={updatedDate}
                              kiss={kiss}
                              setLoading={setLoading}
                            />
                          </th>
                        );
                      })}
                    </tr>
                  );
                })}
              </thead>
              <DragDropContext onDragEnd={handleOnDragEnd}>
                <Droppable droppableId="droppable" direction="vertical">
                  {(provided, snapshot) => (
                    <tbody
                      {...getTableBodyProps()}
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                    >
                      {tickerRow.map((row, i) => {
                        prepareRow(row);
                        return (
                          <Draggable
                            key={`key-${i}`}
                            draggableId={`key-${i}`}
                            index={i}
                          >
                            {(provided, snapshot) => (
                              <tr
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                data-row-index={i}
                                className={`draggable-content`}
                                {...row.getRowProps()}
                              >
                                {row.cells.map((cell, index) => {
                                  return (
                                    <td
                                      className={
                                        kiss
                                          ? "td-non-draggable-kiss"
                                          : "td-non-draggable"
                                      }
                                      data-row-index={i}
                                      style={{
                                        overflow:
                                          cell.column.Header === "Range"
                                            ? "visible"
                                            : "",
                                      }}
                                      {...cell.getCellProps((props, { cell }) =>
                                        cell.column.Header.includes(
                                          "Premium | Signal",
                                        )
                                          ? [props, { style: {} }]
                                          : cell.column.Header.includes(
                                              "Realized",
                                            ) ||
                                            (cell.column.Header.includes(
                                              "Premium",
                                            ) &&
                                              !kiss)
                                          ? [props, { style: { maxWidth: 45 } }]
                                          : props &&
                                            cell.column.Header.includes(
                                              "Description",
                                            )
                                          ? [
                                              props,
                                              {
                                                style: {
                                                  position: "sticky",
                                                  minWidth: 255,
                                                },
                                              },
                                            ]
                                          : cell.column.Header.includes(
                                              "Ticker",
                                            )
                                          ? [
                                              props,
                                              {
                                                style: {
                                                  position: "sticky",
                                                  minWidth: 60,
                                                  ...(kiss
                                                    ? { left: 0 }
                                                    : !kiss &
                                                      (row.allCells[0].column
                                                        .Header !==
                                                        "Description")
                                                    ? { left: 0 }
                                                    : { left: "251px" }),
                                                },
                                              },
                                            ]
                                          : cell.column.Header.includes(
                                              "Closing Price",
                                            )
                                          ? [
                                              props,
                                              {
                                                style: {
                                                  position: "sticky",
                                                  minWidth: 80,
                                                  ...(kiss &
                                                  (row.allCells[2].column
                                                    .Header !==
                                                    "Last Price")
                                                    ? { left: 75 }
                                                    : kiss &
                                                      (row.allCells[2].column
                                                        .Header ===
                                                        "Last Price")
                                                    ? { left: 60 }
                                                    : !kiss &
                                                      (row.allCells[0].column
                                                        .Header !==
                                                        "Description")
                                                    ? { left: "60px" }
                                                    : { left: "310px" }),
                                                },
                                              },
                                            ]
                                          : cell.column.Header.includes(
                                              "Last Price",
                                            )
                                          ? [
                                              props,
                                              {
                                                style: {
                                                  position: "sticky",
                                                  ...(kiss &
                                                  (row.allCells[1].column
                                                    .Header !==
                                                    "Closing Price")
                                                    ? { left: "80px" }
                                                    : kiss &
                                                      (row.allCells[1].column
                                                        .Header ===
                                                        "Closing Price")
                                                    ? { left: "134px" }
                                                    : !kiss &
                                                      (row.allCells[0].column
                                                        .Header !==
                                                        "Description") &
                                                      !kiss &
                                                      (row.allCells[1].column
                                                        .Header !==
                                                        "Closing Price")
                                                    ? { left: "80px" }
                                                    : !kiss &
                                                      (row.allCells[0].column
                                                        .Header ===
                                                        "Description") &
                                                      !kiss &
                                                      (row.allCells[2].column
                                                        .Header !==
                                                        "Closing Price")
                                                    ? { left: "257px" }
                                                    : !kiss &
                                                      (row.allCells[0].column
                                                        .Header !==
                                                        "Description") &
                                                      !kiss &
                                                      (row.allCells[1].column
                                                        .Header ===
                                                        "Closing Price")
                                                    ? { left: "134px" }
                                                    : { left: "386px" }),
                                                },
                                              },
                                            ]
                                          : props,
                                      )}
                                    >
                                      <div
                                        data-row-index={i}
                                        className={`cell ${createCellClassName(
                                          cell.value,
                                        )}  ${
                                          i === 0 && index === 1
                                            ? `guidedTour3 guidedTour6`
                                            : ``
                                        }`}
                                        style={tableStyle(cell)}
                                        onContextMenu={(e) =>
                                          showContextMenu(e, cell)
                                        }
                                      >
                                        <FormatCell
                                          cell={cell}
                                          i={i}
                                          index={index}
                                          data={data}
                                          setRefreshTable={setRefreshTable}
                                          page={page}
                                          kiss={kiss}
                                          longbowPanel={
                                            props.data?.longbow_panel
                                          }
                                          planInterval={planInterval}
                                          setPlanInterval={setPlanInterval}
                                          closingDate={closingDate}
                                          updatedDate={updatedDate}
                                          signalAlertsSubscription={
                                            signalAlertsSubscription
                                          }
                                          setSignalAlertsSubscription={
                                            setSignalAlertsSubscription
                                          }
                                          alertsInterval={alertsInterval}
                                          includeSignal={includeSignal}
                                          setDisableDeleteContextMenu={
                                            setDisableDeleteContextMenu
                                          }
                                          compactMode={compactMode}
                                          setContextMenu={setContextMenu}
                                          bullseye={props?.bullseye}
                                        />
                                      </div>
                                    </td>
                                  );
                                })}
                              </tr>
                            )}
                          </Draggable>
                        );
                      })}
                      {provided.placeholder}
                      {props.data && !props.data?.longbow_panel ? (
                        <TickerContextMenu
                          showMenu={contextMenu}
                          deleteTicker={() => deleteTicker()}
                          clientYDelete={clientYDelete}
                          clientXDelete={clientXDelete}
                          disableDeleteContextMenu={disableDeleteContextMenu}
                        />
                      ) : null}
                    </tbody>
                  )}
                </Droppable>
              </DragDropContext>
              {props?.data && (
                <div
                  style={{
                    marginTop:
                      -105 +
                      scrollOffset -
                      tickerRow.length * (compactMode ? 34.5 : 50.5),
                    marginLeft: 1040,
                    transition: "all 500ms ease-out",
                  }}
                >
                  <TwitterAndArrows
                    basic={localStorage.getItem("view") === "basic"}
                  />
                </div>
              )}
              {props?.bullseye && (
                <div
                  style={{
                    marginTop:
                      -90 +
                      scrollOffset -
                      tickerRow.length * (compactMode ? 36 : 52),
                    marginLeft:
                      1340 -
                      Object.keys(
                        props.tab.section
                          .map((section) => section.column)
                          .flat()
                          .filter((obj) => obj.hidden),
                      ).length *
                        70 -
                      Object.keys(props.tab.section.filter((obj) => obj.hidden))
                        .length *
                        200,
                    transition: "all 500ms ease-out",
                  }}
                >
                  <BullseyeChat />
                </div>
              )}
            </table>
          </div>
        </div>
      ) : (
        !loading &&
        (props?.bullseye ? (
          <div
            style={{
              color: "#fff",
              textAlign: "center",
              marginTop: 150,
            }}
          >
            <i
              className={`justify-center fas fa-exclamation-triangle`}
              style={{
                color: "#d33541",
                fontSize: 50,
                margin: "20px auto",
              }}
            />
            No Bullseye AI trade ideas
          </div>
        ) : (
          <EmptyPanel setRefreshTable={setRefreshTable} tab={props.data} />
        ))
      )}
      {!loading && <Tooltip />}
    </>
  );
}
export default React.memo(Table);
